import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

// Meal Period Configuration — Breakfast / Lunch / Dinner windows for the
// property. Each bill is tagged with the period containing its opened time in
// the Bills & Settlement report (Meal Period column) and the printed bill's
// Session. Active periods may not overlap.
const EMPTY_FORM = { meal_period_name: '', from_time: '', to_time: '', is_active: true };

function KotMealPeriodMaster({ token, selectedProperty }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();

  const chainId = selectedProperty?.chain_id ? String(selectedProperty.chain_id).trim() : '';
  const propertyId = selectedProperty?.id ? String(selectedProperty.id).trim() : '';
  const authHeaders = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': chainId,
    'x-property-id': propertyId,
    ...extra
  });

  useEffect(() => {
    if (!chainId || !propertyId) {
      setRows([]);
      setError('Select a property first from Hotel Config');
      return;
    }
    setError('');
    loadRows();
  }, [token, chainId, propertyId]);

  const loadRows = () => {
    if (!chainId || !propertyId) return;
    fetch('/api/v1/kot/masters/meal-periods', { headers: authHeaders() })
      .then(async (res) => { const data = await res.json().catch(() => ({})); if (!res.ok) throw new Error(data.error || 'Failed to load meal periods'); return data; })
      .then(data => setRows(Array.isArray(data) ? data : []))
      .catch((err) => {
        setRows([]);
        setError(err?.message || 'Failed to load meal periods');
      });
  };

  const resetForm = () => { setEditingId(null); setForm(EMPTY_FORM); };

  const handleSave = async () => {
    if (!chainId || !propertyId) {
      setError('Select a property first from Hotel Config');
      return;
    }
    if (!String(form.meal_period_name || '').trim() || !form.from_time || !form.to_time) {
      setError('Meal Period, From Time and To Time are required');
      return;
    }
    if (form.from_time === form.to_time) {
      setError('From Time and To Time cannot be the same');
      return;
    }
    try {
      const res = await fetch(editingId ? `/api/v1/kot/masters/meal-periods/${editingId}` : '/api/v1/kot/masters/meal-periods', {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(form)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMessage('');
        setError(data.error || 'Save failed');
        return;
      }
      setError('');
      setMessage(editingId ? 'Meal period updated successfully' : 'Meal period created successfully');
      resetForm();
      loadRows();
    } catch {
      setError('Network error');
    }
  };

  const handleEdit = (row) => {
    const normalize = (value) => String(value || '').slice(0, 5);
    setEditingId(row.id);
    setForm({
      meal_period_name: row.meal_period_name || '',
      from_time: normalize(row.from_time),
      to_time: normalize(row.to_time),
      is_active: row.is_active !== false
    });
    setError('');
    setMessage('');
    scrollToForm();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this meal period?')) return;
    try {
      const res = await fetch(`/api/v1/kot/masters/meal-periods/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Delete failed');
        return;
      }
      setError('');
      setMessage('Meal period deleted');
      if (editingId === id) resetForm();
      loadRows();
    } catch {
      setError('Network error');
    }
  };

  const formatTime = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const hhmm = raw.slice(0, 5);
    if (!/^\d{2}:\d{2}$/.test(hhmm)) return raw;
    const [h, m] = hhmm.split(':').map(Number);
    const suffix = h >= 12 ? 'PM' : 'AM';
    const hour12 = (h % 12) || 12;
    return `${hour12}:${String(m).padStart(2, '0')} ${suffix}`;
  };

  const inputStyle = { padding: '0.75rem', borderRadius: '6px', border: '1px solid #dcdde1' };
  const labelStyle = { display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem', color: 'var(--theme-text-muted, #64748b)' };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)' }}>Meal Period Configuration</h2>
      <p style={{ marginTop: 0, color: 'var(--theme-text-muted, #64748b)', fontSize: '0.88rem' }}>
        Each bill is tagged with the meal period that contains its opened time. The tag shows in the Meal Period
        column of the Bills &amp; Settlement report and as Session on the printed bill. A period can run past
        midnight (e.g. 7:00 PM to 1:00 AM). Active periods cannot overlap.
      </p>
      {error && <div className="error-banner">{error}</div>}
      {message && <div className="success-banner">{message}</div>}

      <div className="filter-bar" ref={formRef} style={{ marginBottom: '2rem', ...editHighlightStyle(editHighlight) }}>
        <h3>{editingId ? 'Edit Meal Period' : 'Add Meal Period'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', alignItems: 'end' }}>
          <div>
            <label style={labelStyle}>Meal Period</label>
            <input
              list="meal-period-suggestions"
              placeholder="e.g. Breakfast"
              value={form.meal_period_name}
              onChange={e => setForm({ ...form, meal_period_name: e.target.value })}
              style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
            />
            <datalist id="meal-period-suggestions">
              <option value="Breakfast" />
              <option value="Lunch" />
              <option value="Snacks" />
              <option value="Dinner" />
            </datalist>
          </div>
          <div>
            <label style={labelStyle}>From Time</label>
            <input
              type="time"
              value={form.from_time}
              onChange={e => setForm({ ...form, from_time: e.target.value })}
              style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={labelStyle}>To Time</label>
            <input
              type="time"
              value={form.to_time}
              onChange={e => setForm({ ...form, to_time: e.target.value })}
              style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingBottom: '0.75rem' }}>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={e => setForm({ ...form, is_active: e.target.checked })}
            />
            Active
          </label>
        </div>
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem' }}>
          <button onClick={handleSave} className="btn-primary" style={{ padding: '0.75rem 1.3rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
            {editingId ? 'Update' : 'Add'}
          </button>
          {editingId && (
            <button onClick={() => { resetForm(); setError(''); }} className="btn-muted" style={{ padding: '0.75rem 1.3rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <h3>Meal Period List</h3>
      {rows.length === 0 ? (
        <p>No meal periods configured. Until one is added, the report uses the KOT Time Slots.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr style={{ background: 'var(--theme-primary-dark)', color: '#fff' }}>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Meal Period</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>From Time</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>To Time</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Active</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Last Updated By</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id} style={{ borderBottom: '1px solid #dcdde1' }}>
                  <td style={{ padding: '0.75rem', fontWeight: 600 }}>{row.meal_period_name}</td>
                  <td style={{ padding: '0.75rem' }}>{formatTime(row.from_time)}</td>
                  <td style={{ padding: '0.75rem' }}>{formatTime(row.to_time)}</td>
                  <td style={{ padding: '0.75rem' }}>{row.is_active ? 'Yes' : 'No'}</td>
                  <td style={{ padding: '0.75rem' }}>{row.updated_by || '-'}</td>
                  <td style={{ padding: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => handleEdit(row)} style={{ border: 'none', borderRadius: '4px', padding: '0.45rem 0.8rem', background: '#3498db', color: '#fff', cursor: 'pointer' }}>Edit</button>
                    <button onClick={() => handleDelete(row.id)} className="btn-danger" style={{ border: 'none', borderRadius: '4px', padding: '0.45rem 0.8rem', cursor: 'pointer' }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default KotMealPeriodMaster;
